// src/components/trades/analysis/PlayerStatsSection.js
import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { formatBattingAvg, formatERA } from '../../../services/statFormatUtils';
import { BarChart3 } from 'lucide-react';
import PlayerLink from '../../PlayerLink';

export const PlayerStatsSection = ({ data }) => {
  if (!data) return null;

  const { proposing_gives, receiving_gives, scoring_categories, stats_season } = data;
  const displaySeason = stats_season || new Date().getFullYear();
  
  // Map stat abbreviations to actual stat field names
  const STAT_MAPPINGS = {
    // Hitting
    'R': 'runs',
    'HR': 'home_runs',
    'RBI': 'rbi',
    'SB': 'stolen_bases',
    'AVG': 'batting_avg',
    'OPS': 'ops',
    'OBP': 'obp',
    'SLG': 'slg',
    // Pitching
    'W': 'wins',
    'SV': 'saves',
    'ERA': 'era',
    'WHIP': 'whip',
    'SO': 'strikeouts_pitched',
    'K': 'strikeouts_pitched',
    'QS': 'quality_starts',
    'IP': 'innings_pitched'
  };
  
  // Determine if a player is a pitcher based on position
  const isPitcherPosition = (position) => {
    const pitcherPositions = ['P', 'SP', 'RP'];
    return pitcherPositions.includes(position);
  };
  
  // Format stat value based on stat type
  const formatStatValue = (statAbbr, value) => {
    if (!value && value !== 0) return 'N/A';

    // Rate stats
    if (['AVG', 'OBP', 'SLG', 'OPS', 'WHIP'].includes(statAbbr)) {
      return formatBattingAvg(value);
    }
    if (statAbbr === 'ERA') {
      return formatERA(value);
    }

    // IP (innings pitched) - show one decimal
    if (statAbbr === 'IP') {
      return value.toFixed(1);
    }

    // Counting stats (whole numbers)
    return Math.round(value);
  };

  const renderPlayerCard = (player, isGiving) => {
    const currentStats = player.current_season_stats || {};
    const lastSeasonStats = player.last_season_stats || {};
    
    // Extract hitting or pitching stats
    const hitting = currentStats.hitting || {};
    const pitching = currentStats.pitching || {};
    const isPitcher = isPitcherPosition(player.position);
    
    // Get the appropriate stat categories from league settings
    const statsToShow = isPitcher 
      ? (scoring_categories?.pitching || ['W', 'SV', 'ERA', 'WHIP', 'SO', 'QS'])
      : (scoring_categories?.hitting || ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS']);

    return (
      <div key={player.player_id} className={`p-4 rounded-lg border ${dynastyTheme.classes.border.neutral} bg-gray-800/30`}>
        <div className="flex justify-between items-start mb-3">
          <div>
            <h5 className="font-bold text-white"><PlayerLink mlbId={player.player_id} name={player.name} style={{ fontWeight: 700, color: '#ffffff' }} /></h5>
            <p className="text-sm text-gray-400">
              {player.position} • {player.team}
            </p>
          </div>
          <span className={`text-xs px-2 py-1 rounded ${isGiving ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
            {isGiving ? 'Giving' : 'Getting'}
          </span>
        </div>

        <div className="space-y-2 text-sm">
          <div className={`font-semibold ${dynastyTheme.classes.text.primary}`}>{displaySeason} Stats</div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            {statsToShow.map(statAbbr => {
              const statField = STAT_MAPPINGS[statAbbr] || statAbbr.toLowerCase();
              const statSource = isPitcher ? pitching : hitting;
              const value = statSource[statField];
              
              return (
                <div key={statAbbr}>
                  <span className="text-gray-400">{statAbbr}:</span>{' '}
                  <span className="text-white">{formatStatValue(statAbbr, value)}</span>
                </div>
              );
            })}
          </div>

          {(lastSeasonStats.hitting || lastSeasonStats.pitching) ? (
            <>
              <div className="font-semibold text-gray-400 mt-3">{displaySeason - 1} Stats</div>
              <div className="grid grid-cols-3 gap-2 text-xs text-gray-400">
                {statsToShow.map(statAbbr => {
                  const statField = STAT_MAPPINGS[statAbbr] || statAbbr.toLowerCase();
                  const lastHitting = lastSeasonStats.hitting || {};
                  const lastPitching = lastSeasonStats.pitching || {};
                  const statSource = isPitcher ? lastPitching : lastHitting;
                  const value = statSource[statField];
                  
                  return (
                    <div key={statAbbr}>
                      {statAbbr}: {formatStatValue(statAbbr, value)}
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className={`p-4 ${dynastyTheme.classes.bg.darkCard} rounded-lg border ${dynastyTheme.classes.border.neutral}`}>
      <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.primary} mb-4 flex items-center gap-2`}>
        <BarChart3 className="w-5 h-5" />
        Player Comparison
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-md font-semibold text-red-400 mb-3">You're Giving Away</h4>
          <div className="space-y-3">
            {proposing_gives && proposing_gives.length > 0 ? (
              proposing_gives.map(player => renderPlayerCard(player, true))
            ) : (
              <div className="text-gray-500 text-sm">No players</div>
            )}
          </div>
        </div>

        <div>
          <h4 className="text-md font-semibold text-emerald-400 mb-3">You're Getting</h4>
          <div className="space-y-3">
            {receiving_gives && receiving_gives.length > 0 ? (
              receiving_gives.map(player => renderPlayerCard(player, false))
            ) : (
              <div className="text-gray-500 text-sm">No players</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerStatsSection;
